import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FadeUp, MaskedLineReveal } from "./motion/Reveal";
import { TEST_IDS } from "../constants/testIds";
import { api, formatApiError } from "../lib/api";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";

const INITIAL = { name: "", email: "", phone: "", message: "" };

export function Contatti() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data?.message || "Grazie, ti risponderemo al più presto.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contatti"
      data-testid={TEST_IDS.contatti.section}
      className="relative pt-28 md:pt-44 border-t border-white/10 bg-[#0A0C10]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          {/* Left: form + eyebrow */}
          <div className="col-span-12 md:col-span-6 order-2 md:order-1">
            <FadeUp>
              <span className="eyebrow">
                <span className="inline-block mr-3 w-6 h-px bg-[#E31E24] align-middle" />
                Contatti
              </span>
            </FadeUp>
            <h2 className="mt-6 mb-12 font-display font-black uppercase tracking-tighter leading-[0.92] text-3xl sm:text-4xl md:text-5xl">
              <MaskedLineReveal>Invia</MaskedLineReveal>
              <MaskedLineReveal delay={0.15}>
                <span className="font-editorial italic font-light">una</span>
              </MaskedLineReveal>
              <MaskedLineReveal delay={0.3}>richiesta.</MaskedLineReveal>
            </h2>

            <form
              data-testid={TEST_IDS.contatti.form}
              onSubmit={onSubmit}
              className="space-y-3"
            >
              <FieldRow label="01 · Nome" htmlFor="name">
                <input
                  id="name"
                  data-testid={TEST_IDS.contatti.name}
                  value={form.name}
                  onChange={set("name")}
                  required
                  placeholder="Nome e cognome"
                  className="input-underline"
                />
              </FieldRow>
              <FieldRow label="02 · Email" htmlFor="email">
                <input
                  id="email"
                  type="email"
                  data-testid={TEST_IDS.contatti.email}
                  value={form.email}
                  onChange={set("email")}
                  required
                  placeholder="nome@dominio.it"
                  className="input-underline"
                />
              </FieldRow>
              <FieldRow label="03 · Telefono" htmlFor="phone">
                <input
                  id="phone"
                  data-testid={TEST_IDS.contatti.phone}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="Facoltativo"
                  className="input-underline"
                />
              </FieldRow>
              <FieldRow label="04 · Messaggio" htmlFor="message">
                <textarea
                  id="message"
                  data-testid={TEST_IDS.contatti.message}
                  value={form.message}
                  onChange={set("message")}
                  required
                  rows={4}
                  placeholder="Ci racconti del lavoro, del cantiere, dei metri quadri..."
                  className="input-underline resize-none"
                />
              </FieldRow>
              <div className="pt-4">
                <button
                  type="submit"
                  data-testid={TEST_IDS.contatti.submit}
                  className="btn-heat disabled:opacity-60 disabled:pointer-events-none"
                  disabled={loading}
                >
                  {loading ? "Invio in corso…" : "Invia richiesta"}
                  <span aria-hidden>→</span>
                </button>
              </div>
            </form>
          </div>

          {/* Right: info + wordmark */}
          <div className="col-span-12 md:col-span-6 order-1 md:order-2">
            <div className="grid grid-cols-1 gap-8">
              <InfoRow
                icon={<Phone size={16} />}
                label="Telefoni"
                lines={[
                  { href: "tel:+393488546586", text: "+39 348 85 46 586" },
                  { href: "tel:+393924856166", text: "+39 392 48 56 166" },
                ]}
              />
              <InfoRow
                icon={<MessageCircle size={16} />}
                label="WhatsApp"
                lines={[
                  {
                    href: "https://wa.me/393488546586",
                    text: "Chatta con noi ora",
                    external: true,
                  },
                ]}
              />
              <InfoRow
                icon={<Mail size={16} />}
                label="Email"
                lines={[{ href: "mailto:info@foxtherm.it", text: "info@foxtherm.it" }]}
              />
              <InfoRow
                icon={<MapPin size={16} />}
                label="Sede"
                lines={[
                  { text: "Via Santa Teresa, 53" },
                  { text: "37135 Verona (VR) · Italia" },
                ]}
              />
              <div className="border-t border-white/10 pt-8 text-xs font-mono uppercase tracking-[0.2em] text-white/40 space-y-1">
                <div>REA VR-434645</div>
                <div>C.F. / P.IVA 04604320236</div>
                <div>SDI W7YVJK9</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Editorial inverted wordmark moment — light slab with real logo */}
      <div className="mt-8 border-t border-white/10" />

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
          <div>© {new Date().getFullYear()} Foxtherm S.r.l.s — Tutti i diritti riservati</div>
          <div className="flex gap-6">
            <span>Impianti idrosanitari, termici, HVAC</span>
            <a href="/admin/login" className="hover:text-white transition-colors">
              Area riservata
            </a>
          </div>
        </div>
      </footer>
    </section>
  );
}

function FieldRow({ label, htmlFor, children }) {
  return (
    <div className="grid grid-cols-12 gap-4 border-b border-white/10 pb-1 items-end">
      <label
        htmlFor={htmlFor}
        className="col-span-12 sm:col-span-3 font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 pb-4"
      >
        {label}
      </label>
      <div className="col-span-12 sm:col-span-9">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, lines }) {
  return (
    <div className="grid grid-cols-12 gap-4 border-b border-white/10 pb-6">
      <div className="col-span-12 sm:col-span-4 flex items-center gap-3 text-white/70">
        <span className="w-8 h-8 border border-white/20 flex items-center justify-center text-[#E31E24]">
          {icon}
        </span>
        <span className="font-mono text-[10px] tracking-[0.28em] uppercase">
          {label}
        </span>
      </div>
      <div className="col-span-12 sm:col-span-8 space-y-1">
        {lines.map((l, i) =>
          l.href ? (
            <a
              key={i}
              href={l.href}
              target={l.external ? "_blank" : undefined}
              rel={l.external ? "noopener noreferrer" : undefined}
              className="block font-display font-black text-xl md:text-2xl tracking-tight uppercase hover:text-[#E31E24] transition-colors"
            >
              {l.text}
            </a>
          ) : (
            <div
              key={i}
              className="font-display font-black text-xl md:text-2xl tracking-tight uppercase"
            >
              {l.text}
            </div>
          )
        )}
      </div>
    </div>
  );
}
